import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ExternalLink, CreditCard, Calendar, RefreshCw } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const SubscriptionManagement = () => {
  const {
    subscribed,
    status,
    plan_slug,
    current_period_end,
    cancel_at_period_end,
    loading,
    error,
    checkSubscription,
    openCustomerPortal
  } = useSubscription();

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'trialing':
        return 'bg-blue-500';
      case 'past_due':
        return 'bg-yellow-500';
      case 'canceled':
      case 'unpaid':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'trialing':
        return 'Período de teste';
      case 'past_due':
        return 'Pagamento atrasado';
      case 'canceled':
        return 'Cancelada';
      case 'unpaid':
        return 'Não paga';
      default:
        return 'Inativa';
    }
  };

  const getPlanName = (slug?: string) => {
    switch (slug) {
      case 'basic':
        return 'Plano Básico';
      default:
        return 'Nenhum plano';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Gerenciar Assinatura
          </CardTitle>
          <CardDescription>Gerencie sua assinatura e pagamentos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span className="ml-2">Carregando informações da assinatura...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Gerenciar Assinatura
          </CardTitle>
          <CardDescription>Gerencie sua assinatura e pagamentos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={checkSubscription} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Gerenciar Assinatura
        </CardTitle>
        <CardDescription>Gerencie sua assinatura e pagamentos</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Status da Assinatura */}
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Status da Assinatura</h3>
              <Button onClick={checkSubscription} variant="ghost" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge className={`${getStatusColor(status)} text-white`}>
                {getStatusText(status)}
              </Badge>
              {cancel_at_period_end && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                  Cancelamento programado
                </Badge>
              )}
            </div>
          </div>

          <Separator />

          {/* Informações do Plano */}
          <div className="grid gap-2">
            <h4 className="font-medium text-sm">Plano Atual</h4>
            <p className="text-lg font-semibold">{getPlanName(plan_slug)}</p>
          </div>

          {/* Data de Renovação */}
          {current_period_end && (
            <>
              <Separator />
              <div className="grid gap-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {cancel_at_period_end ? 'Expira em' : 'Próxima cobrança'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(current_period_end), "dd 'de' MMMM 'de' yyyy", { 
                    locale: ptBR 
                  })}
                </p>
              </div>
            </>
          )}

          <Separator />

          {/* Ações */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Ações</h4>
            
            {subscribed ? (
              <Button 
                onClick={openCustomerPortal}
                className="w-full"
                variant="outline"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Gerenciar no Portal Stripe
              </Button>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Você não possui uma assinatura ativa
                </p>
                <Button 
                  onClick={() => window.location.href = '/#pricing'}
                  className="w-full"
                >
                  Ver Planos Disponíveis
                </Button>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              No portal do Stripe você pode atualizar seus dados de pagamento, 
              alterar planos, baixar faturas e cancelar sua assinatura.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionManagement;
